import { Divider, Flex, Typography } from "antd"
import React from "react"

import { excludeDeletedAccounts } from "../../../../constants/deleted-account-ui.ts"
import { buildEntityLabelMap } from "../../../../utils/select/entity-labels.ts"
import { ManagersSelector } from "../../ProjectAddPage/components/sections/StakeholdersAndRolesSection/ManagersSelector.tsx"
import { type Project, type ProjectManager } from "../../types/project.types.ts"

const { Text } = Typography

export interface AssignedManager {
  id: string
  is_main: boolean
}

export type ManagerField = "project_managers" | "sales_manager"

const toAssignedManagers = (
  managers?: ProjectManager[] | null
): AssignedManager[] =>
  excludeDeletedAccounts(managers ?? [])?.map((m) => ({
    id: m.id,
    is_main: !!m?.is_main
  })) ?? []

interface ProjectManagersFieldsProps {
  project: Project
  filterOptions: Record<string, any>
  assignedPMAllowed: boolean
  assignedSalesAllowed: boolean
  onChange: (field: ManagerField, next: AssignedManager[]) => void
}

/**
 * Project Manager and Sales Manager pickers. Each saves as soon as it changes,
 * the same way the status pill beside them does. Shared by the Edit Details
 * page's header and the Overview tab.
 */
export function ProjectManagersFields({
  project,
  filterOptions,
  assignedPMAllowed,
  assignedSalesAllowed,
  onChange
}: ProjectManagersFieldsProps) {
  return (
    <Flex align={"center"} gap={"middle"}>
      <Flex vertical gap={0} className={"w-[170px]"}>
        <Text type={"secondary"} className={"text-xs"}>
          Project Manager
        </Text>
        <ManagersSelector
          size={"small"}
          maxTagCount={1}
          disabled={!assignedPMAllowed}
          placeholder={"Enter project manager"}
          options={filterOptions?.project_manager}
          fallbackLabels={buildEntityLabelMap(project?.project_managers)}
          value={toAssignedManagers(project?.project_managers)}
          onChange={(next) => onChange("project_managers", next)}
        />
      </Flex>

      <Divider
        type={"vertical"}
        style={{ height: 40, margin: 0, borderInlineStartWidth: 1 }}
      />

      <Flex vertical gap={0} className={"w-[170px]"}>
        <Text type={"secondary"} className={"text-xs"}>
          Sales Manager
        </Text>
        <ManagersSelector
          size={"small"}
          maxTagCount={1}
          disabled={!assignedSalesAllowed}
          placeholder={"Enter sales manager"}
          options={filterOptions?.sales_manager}
          fallbackLabels={buildEntityLabelMap(project?.sales_manager)}
          value={toAssignedManagers(project?.sales_manager)}
          onChange={(next) => onChange("sales_manager", next)}
        />
      </Flex>
    </Flex>
  )
}
